import React, { useState } from 'react';
import { Check, ExternalLink, FilePlus2, GitBranch, History, KeyRound, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { PageHeader } from '../../components/admin/PageHeader';
import { Panel } from '../../components/Panel';
import { StatusBadge } from '../../components/StatusBadge';
import { useDemo } from '../../contexts/DemoContext';
import { sampleCase } from '../../data/sampleCase';
import { toBn } from '../../utils/bangla';
const statusOptions = ['যাচাইকৃত', 'অবৈধ', 'বাতিল', 'নতুন সংস্করণ দ্বারা প্রতিস্থাপিত', 'পর্যালোচনাধীন'];
interface Version {
  v: number;
  docId: string;
  status: string;
  issued: string;
  note: string;
}
interface HistoryItem {
  at: string;
  by: string;
  from: string;
  to: string;
  reason: string;
}
const editableFields: {
  key: 'hearingDate' | 'hearingTime' | 'address' | 'owner';
  label: string;
}[] = [{
  key: 'hearingDate',
  label: 'হাজিরার তারিখ'
}, {
  key: 'hearingTime',
  label: 'হাজিরার সময়'
}, {
  key: 'address',
  label: 'ঠিকানা'
}, {
  key: 'owner',
  label: 'মালিকের নাম'
}];
export function StatusManagement() {
  const {
    user
  } = useDemo();
  const [versions, setVersions] = useState<Version[]>([{
    v: 1,
    docId: 'DMP-DOC-2025-0748-V1',
    status: 'যাচাইকৃত',
    issued: '১৫/০৬/২০২৫',
    note: 'মূল নিরাপদ নোটিশ'
  }]);
  const [history, setHistory] = useState<HistoryItem[]>([{
    at: '১৫/০৬/২০২৫, ০৯:৪২',
    by: 'সিস্টেম',
    from: '—',
    to: 'যাচাইকৃত',
    reason: 'অনুমোদনের পর নিরাপদ নোটিশ V১ ইস্যু'
  }, {
    at: '১৫/০৬/২০২৫, ০৯:৪০',
    by: 'এসি মোঃ শাহরিয়ার আলম',
    from: 'যাচাই সম্পন্ন',
    to: 'অনুমোদিত',
    reason: 'নিরাপদ নোটিশ তৈরির অনুমতি'
  }]);
  const current = versions[versions.length - 1];
  const [newStatus, setNewStatus] = useState(statusOptions[4]);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [wizard, setWizard] = useState(0); // 0 closed, 1 edit, 2 approval, 3 done
  const [field, setField] = useState<(typeof editableFields)[number]['key']>('hearingDate');
  const [value, setValue] = useState('০৯/০৭/২০২৫');
  const [changeReason, setChangeReason] = useState('নাগরিকের আবেদনের প্রেক্ষিতে হাজিরার তারিখ পরিবর্তন');
  const [pin, setPin] = useState('');
  const now = '১৩/১০/২০২৬, ' + toBn(new Date().toTimeString().slice(0, 5));
  const applyStatus = () => {
    if (!reason.trim()) return setErr('অবস্থা পরিবর্তনের কারণ উল্লেখ করা আবশ্যক।');
    if (newStatus === current.status) return setErr('নতুন অবস্থা বর্তমান অবস্থা থেকে ভিন্ন হতে হবে।');
    setHistory(h => [{
      at: now,
      by: user?.name ?? '',
      from: current.status,
      to: newStatus,
      reason
    }, ...h]);
    setVersions(vs => vs.map(v => v.v === current.v ? {
      ...v,
      status: newStatus
    } : v));
    setReason('');
    setErr('');
    toast.success('নোটিশের অবস্থা হালনাগাদ হয়েছে', {
      description: `${current.status} → ${newStatus}`
    });
  };
  const generateVersion = () => {
    if (pin.length !== 6) return toast.error('৬ অঙ্কের অনুমোদন PIN দিন');
    const nv = current.v + 1;
    setVersions(vs => [...vs.map(v => v.v === current.v ? {
      ...v,
      status: 'নতুন সংস্করণ দ্বারা প্রতিস্থাপিত'
    } : v), {
      v: nv,
      docId: `DMP-DOC-2025-0748-V${nv}`,
      status: 'যাচাইকৃত',
      issued: '১৩/১০/২০২৬',
      note: `${editableFields.find(f => f.key === field)?.label} হালনাগাদ: ${value}`
    }]);
    setHistory(h => [{
      at: now,
      by: 'সিস্টেম',
      from: '—',
      to: 'যাচাইকৃত',
      reason: `নতুন সংস্করণ V${toBn(nv)} ইস্যু; QR টোকেন পুনঃতৈরি`
    }, {
      at: now,
      by: 'সিস্টেম',
      from: 'যাচাইকৃত',
      to: 'নতুন সংস্করণ দ্বারা প্রতিস্থাপিত',
      reason: `V${toBn(current.v)} প্রতিস্থাপিত — ${changeReason}`
    }, ...h]);
    setWizard(3);
  };
  return <div data-id="element-1618" data-mp="332q87jsbt0xh">
      <PageHeader screen="WEB SCREEN 13" title="নোটিশের অবস্থা ব্যবস্থাপনা" subtitle={`মামলা নং ${sampleCase.caseNo} • সকল পূর্ববর্তী সংস্করণ ও অডিট ট্রেইল সংরক্ষিত`} actions={<Button onClick={() => {
      setWizard(1);
      setPin('');
    }} data-id="element-1620" data-mp="2t522jyt6m90j"><FilePlus2 className="h-4 w-4" data-id="element-1621" data-mp="2t4aklqsic9sq" /> নোটিশ হালনাগাদ / নতুন সংস্করণ</Button>} data-id="element-1619" data-mp="3326s88m8kmm3" />

      <div className="grid gap-6 xl:grid-cols-[1fr_380px]" data-id="element-1622" data-mp="2y229sd1rpht8">
        <div className="space-y-6" data-id="element-1623" data-mp="2je7jf3b8fpjb">
          <Panel title="নোটিশ সংস্করণসমূহ" subtitle="সংস্করণ নিয়ন্ত্রণ" actions={<GitBranch className="h-4 w-4 text-slate-400" data-id="element-1625" data-mp="2o5pkrsh63s4t" />} data-id="element-1624" data-mp="2jd66suekiuzh">
            <ol className="space-y-3" data-id="element-1626" data-mp="2ae173kr9svlq">
              {[...versions].reverse().map(v => <li key={v.v} className={`flex flex-wrap items-center gap-4 rounded-lg border p-4 ${v.v === current.v ? 'border-[#003B71] bg-[#E8F1FA]/40' : 'border-border bg-slate-50'}`} data-id="element-1627" data-mp="2ac7y3yt6pzak">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-lg font-mono text-sm font-bold ${v.v === current.v ? 'bg-[#003B71] text-white' : 'bg-slate-200 text-slate-600'}`} data-id="element-1628" data-mp="20hy2m88y4kvm">V{toBn(v.v)}</span>
                  <div className="min-w-0 flex-1" data-id="element-1629" data-mp="1hrkqvaf4anxn">
                    <div className="font-mono text-sm font-semibold" data-id="element-1630" data-mp="1ht5hioyb9mq8">{v.docId}</div>
                    <div className="text-xs text-slate-500" data-id="element-1631" data-mp="1vsdhvl67n0w0">ইস্যু: {v.issued} • {v.note}</div>
                  </div>
                  <StatusBadge label={v.status} data-id="element-1632" data-mp="1qqo96c6gmzb3" />
                  {v.status === 'নতুন সংস্করণ দ্বারা প্রতিস্থাপিত' && <Link to="/citizen/superseded" className="flex items-center gap-1 text-xs font-medium text-[#1769AA] hover:underline" data-id="element-1633" data-mp="24pnvveaex44m"><ExternalLink className="h-3.5 w-3.5" data-id="element-1634" data-mp="2ehtvna6wopud" /> নাগরিক ভিউ</Link>}
                </li>)}
            </ol>
          </Panel>

          <Panel title="অবস্থার ইতিহাস ও অডিট ট্রেইল" actions={<History className="h-4 w-4 text-slate-400" data-id="element-1636" data-mp="2ofhqs18fbzd2" />} bodyClassName="overflow-x-auto" data-id="element-1635" data-mp="29jiml1ofqjd7">
            <table className="w-full min-w-[720px] text-sm" data-id="element-1637" data-mp="2jhgb7qa7ii6c">
              <thead className="bg-[#F4F7FB] text-left text-xs text-slate-500" data-id="element-1638" data-mp="2jetup15c8sbz">
                <tr data-id="element-1639" data-mp="2qlploqafd6w7"><th className="px-4 py-2.5" data-id="element-1640" data-mp="2qlxxsfyq486c">তারিখ ও সময়</th><th className="px-4 py-2.5" data-id="element-1641" data-mp="2viy2amfxd2vc">কর্মকর্তা</th><th className="px-4 py-2.5" data-id="element-1642" data-mp="2vjzdcqwxxpd2">পূর্ববর্তী</th><th className="px-4 py-2.5" data-id="element-1643" data-mp="30iaqpbmg2i01">পরিবর্তিত</th><th className="px-4 py-2.5" data-id="element-1644" data-mp="30h9e32ps5ng7">কারণ</th></tr>
              </thead>
              <tbody data-id="element-1645" data-mp="359j0i8ros5dq">
                {history.map((h, i) => <tr key={i} className="border-t border-border" data-id="element-1646" data-mp="35au3i6db593s" data-mpi={i}>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs" data-id="element-1647" data-mp="3a7c7v54q75fl">{h.at}</td>
                    <td className="px-4 py-2.5" data-id="element-1648" data-mp="3e8nzfg75u0qf">{h.by}</td>
                    <td className="px-4 py-2.5" data-id="element-1649" data-mp="3j8ser2wc0pf2">{h.from === '—' ? '—' : <StatusBadge label={h.from} data-id="element-1650" data-mp="3ja3ix3tovz6p" />}</td>
                    <td className="px-4 py-2.5" data-id="element-1651" data-mp="3o7mxy5y7ofzp"><StatusBadge label={h.to} data-id="element-1652" data-mp="3o6ty7oxo6qr2" /></td>
                    <td className="px-4 py-2.5 text-slate-600" data-id="element-1653" data-mp="3o5iu1o0bbgzf">{h.reason}</td>
                  </tr>)}
              </tbody>
            </table>
          </Panel>
        </div>

        <Panel title="অবস্থা পরিবর্তন" subtitle="শুধুমাত্র অনুমোদিত কর্মকর্তাদের জন্য" data-id="element-1654" data-mp="1tvyfhtsrtvak">
          <div className="mb-4 rounded-md bg-[#F4F7FB] p-3 text-sm" data-id="element-1655" data-mp="1mbnunuzc7839">
            বর্তমান অবস্থা (V{toBn(current.v)}): <StatusBadge label={current.status} className="ml-1" data-id="element-1656" data-mp="1hhjk71qv4hxf" />
          </div>
          <fieldset className="space-y-2" data-id="element-1657" data-mp="1cl1h06b6krn7">
            <legend className="mb-1 text-sm font-medium" data-id="element-1658" data-mp="17lox9uflw0ou">নতুন অবস্থা</legend>
            {statusOptions.map(s => <label key={s} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${newStatus === s ? 'border-[#003B71] bg-[#E8F1FA]/50' : 'border-border'}`} data-id="element-1659" data-mp="259gwsv24zdzl">
                <input type="radio" name="status" className="accent-[#003B71]" checked={newStatus === s} onChange={() => {
              setNewStatus(s);
              setErr('');
            }} data-id="element-1660" data-mp="1vahv5wfsgim3" />
                {s}
              </label>)}
          </fieldset>
          <label className="mt-4 block" data-id="element-1661" data-mp="jwnno3v1ejhz">
            <span className="mb-1 block text-sm font-medium" data-id="element-1662" data-mp="ewrelq7hg1vk">কারণ <span className="text-[#D93036]" data-id="element-1663" data-mp="evzxfkemu6ot">*</span></span>
            <Textarea rows={3} value={reason} onChange={e => {
            setReason(e.target.value);
            setErr('');
          }} placeholder="অবস্থা পরিবর্তনের কারণ লিখুন" data-id="element-1664" data-mp="14l6laemejvgm" />
          </label>
          {err && <p className="mt-1 text-xs text-[#D93036]" data-id="element-1665" data-mp="19hnbrfh2qlwv">{err}</p>}
          <Button className="mt-4 w-full" onClick={applyStatus} data-id="element-1666" data-mp="vljsgh7qnxz6"><RefreshCw className="h-4 w-4" data-id="element-1667" data-mp="zlk8u7eeej8v" /> অবস্থা হালনাগাদ করুন</Button>
        </Panel>
      </div>

      <Dialog open={wizard > 0} onOpenChange={o => !o && setWizard(0)} data-id="element-1668" data-mp="1nboe0j78ir1z">
        <DialogContent className="sm:max-w-lg" data-id="element-1669" data-mp="1ndr04s59o01f">
          <DialogHeader data-id="element-1670" data-mp="1s9g375ghvz2h">
            <DialogTitle data-id="element-1671" data-mp="1s9zo8v1fz3kq">নোটিশ হালনাগাদ — নতুন সংস্করণ</DialogTitle>
            <DialogDescription data-id="element-1672" data-mp="1ejw5ie1rts01">ধাপ {toBn(Math.min(wizard, 3))}/৩ • {['', 'তথ্য হালনাগাদ', 'কর্মকর্তার অনুমোদন', 'নতুন সংস্করণ তৈরি'][wizard]}</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-4" data-id="element-1673" data-mp="269qw0e1d7nqa">
            <div className="mb-4 flex gap-1.5" data-id="element-1674" data-mp="268pk67ciyx7i">
              {[1, 2, 3].map(s => <span key={s} className={`h-1.5 flex-1 rounded-full ${wizard >= s ? 'bg-[#003B71]' : 'bg-slate-200'}`} data-id="element-1675" data-mp="2b18v0wjtve8m" />)}
            </div>
            {wizard === 1 && <div className="space-y-3" data-id="element-1676" data-mp="3qsb4ds02zgwm">
                <label className="block text-sm" data-id="element-1677" data-mp="3qrjkvfjrd9mp">
                  <span className="mb-1 block font-medium" data-id="element-1678" data-mp="9btl5elt3rhz">হালনাগাদের ক্ষেত্র</span>
                  <select value={field} onChange={e => setField(e.target.value as typeof field)} className="h-9 w-full rounded-md border border-input px-3" data-id="element-1679" data-mp="9dw6vmfxeygw">
                    {editableFields.map(f => <option key={f.key} value={f.key} data-id="element-1680" data-mp="3css8pyoghy17">{f.label}</option>)}
                  </select>
                </label>
                <div className="text-xs text-slate-500" data-id="element-1681" data-mp="37vtdtdwyxn1y">বর্তমান মান: {sampleCase[field]}</div>
                <label className="block text-sm" data-id="element-1682" data-mp="3mod6tlqeme5o"><span className="mb-1 block font-medium" data-id="element-1683" data-mp="3hr3jcbmksckh">নতুন মান</span><Input value={value} onChange={e => setValue(e.target.value)} data-id="element-1684" data-mp="3htpq491n6o1n" /></label>
                <label className="block text-sm" data-id="element-1685" data-mp="11ynt5c1fsyek"><span className="mb-1 block font-medium" data-id="element-1686" data-mp="x1e5o1xlywtd">কারণ</span><Textarea rows={2} value={changeReason} onChange={e => setChangeReason(e.target.value)} data-id="element-1687" data-mp="x0mc0wnkn95q" /></label>
              </div>}
            {wizard === 2 && <div className="space-y-3 text-sm" data-id="element-1688" data-mp="34wdyvlfgxby1">
                <div className="rounded-md bg-[#F4F7FB] p-3" data-id="element-1689" data-mp="34xp29k504hom">
                  <div data-id="element-1690" data-mp="303bcpyd0t4rv"><b data-id="element-1691" data-mp="3031mc9o2cvln">{editableFields.find(f => f.key === field)?.label}:</b> <s className="text-slate-400" data-id="element-1692" data-mp="2v61g9yr7rsuj">{sampleCase[field]}</s> → <span className="font-semibold text-[#006A4E]" data-id="element-1693" data-mp="2v59mmth6g56w">{value}</span></div>
                  <div className="mt-1 text-slate-600" data-id="element-1694" data-mp="2q7zwfblyrpi0">কারণ: {changeReason}</div>
                </div>
                <p data-id="element-1695" data-mp="2m3a4tyyc8x2w">অনুমোদনকারী কর্মকর্তা: <b data-id="element-1696" data-mp="2m77msh96jil7">এসি মোঃ শাহরিয়ার আলম</b></p>
                <label className="block" data-id="element-1697" data-mp="2h6teqz7kw8n6">
                  <span className="mb-1 flex items-center gap-1.5 font-medium" data-id="element-1698" data-mp="2h7us5ac2h782"><KeyRound className="h-4 w-4" data-id="element-1699" data-mp="2c7p381x6lyto" /> অনুমোদন PIN (ডেমো: যেকোনো ৬ অঙ্ক)</span>
                  <Input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} className="font-mono tracking-[0.4em]" placeholder="••••••" data-id="element-1700" data-mp="fdc9g26x7jvl" />
                </label>
              </div>}
            {wizard === 3 && <div className="text-center" data-id="element-1701" data-mp="mxwmlv8vgqbr">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#E7F4EC]" data-id="element-1702" data-mp="mwv0mvqbijf9"><Check className="h-7 w-7 text-[#16834A]" data-id="element-1703" data-mp="rpo4zihvjpqt" /></div>
                <p className="font-semibold text-[#003B71]" data-id="element-1704" data-mp="wng715c8gs8q">নতুন সংস্করণ {current.docId} তৈরি হয়েছে</p>
                <p className="mt-1 text-sm text-slate-600" data-id="element-1705" data-mp="3v5lfjsjy6zpo">পূর্ববর্তী সংস্করণ "নতুন সংস্করণ দ্বারা প্রতিস্থাপিত" হিসেবে চিহ্নিত। নাগরিক পুরাতন QR স্ক্যান করলে নতুন সংস্করণের নির্দেশনা দেখবেন।</p>
              </div>}
          </div>
          <DialogFooter data-id="element-1706" data-mp="8wk69ipsumpc">
            {wizard === 1 && <Button onClick={() => setWizard(2)} data-id="element-1707" data-mp="dx89d2raytzx">অনুমোদনের জন্য পাঠান</Button>}
            {wizard === 2 && <><Button variant="outline" onClick={() => setWizard(1)} data-id="element-1708" data-mp="1kdaj3koqacrx">পূর্ববর্তী</Button><Button onClick={generateVersion} data-id="element-1709" data-mp="1kd0sbuvv01l6">অনুমোদন ও নতুন সংস্করণ তৈরি</Button></>}
            {wizard === 3 && <Button onClick={() => setWizard(0)} data-id="element-1710" data-mp="1pehppgf4x5x0">সম্পন্ন</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}